import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { LocalStore } from '../store/local';

const THEME_KEY: Readonly<string> = 'my_group_theme';
const DATA_COLOR_MODE: Readonly<string> = 'data-color-mode';

export type ColorMode = 'light' | 'dark';

const DEFAULT_MODE: ColorMode = 'light';
const isColorMode = (value: string | null): value is ColorMode => value === 'light' || value === 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private localStore: LocalStore = inject(LocalStore);
  private document: Document = inject(DOCUMENT);

  private readonly _mode: WritableSignal<ColorMode> = signal(this.readStoredMode());

  readonly mode: Signal<ColorMode> = this._mode.asReadonly();

  constructor() {
    effect((): void => {
      const mode: ColorMode = this._mode();
      this.document.documentElement.setAttribute(DATA_COLOR_MODE, mode);
      this.localStore.set(THEME_KEY, mode);
    });
  }

  toggle(): void {
    this._mode.update((mode: ColorMode): ColorMode => (mode === 'light' ? 'dark' : 'light'));
  }

  private readStoredMode(): ColorMode {
    const stored: string | null = this.localStore.get<string>(THEME_KEY);
    return isColorMode(stored) ? stored : DEFAULT_MODE;
  }
}
